import React, { useEffect, useRef, useState } from "react";
import {
  Col,
  Container,
  Row,
  Card,
  Spinner,
  Pagination,
} from "react-bootstrap";
import { useLocation } from "react-router-dom";
import { useSupabaseApp } from "../../SupabaseApp";
import { BiAnalyse } from "react-icons/bi";
import { AiOutlineCloseCircle } from "react-icons/ai";
import PRNewswireLogo from "../../../assets/dashboard/prn_cision_logo.png";
import "../dashboard/rss-feed.css";

/**
 * Highlight search keywords in text with mark tags
 * Splits text by keyword occurrences and wraps matches in <mark> elements
 * Case-insensitive highlighting
 * 
 * @param {string} text - Text to highlight
 * @param {string} keyword - Term to search for and highlight
 * @returns {Array|string} Text with highlighted keywords or plain text if no keyword
 */
const highlightText = (text, keyword) => {
  if (!keyword) return text;

  const escaped = keyword.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const regex = new RegExp(`(${escaped})`, "gi");

  return text.split(regex).map((part, i) =>
    regex.test(part) ? (
      <mark key={i} className="rssHighlight">
        {part}
      </mark>
    ) : (
      part
    )
  );
};

/**
 * FeedCard Component
 * 
 * Displays a single press release/news item with title, date, summary text,
 * optional image, and search term highlighting. Extracts clean text from HTML
 * descriptions and handles missing images with fallback placeholder.
 * 
 * @param {Object} props - Component props
 * @param {Object} props.item - Feed item with title, description, pubDate, link, img
 * @param {string} props.searchTerm - Search term to highlight in text
 * @returns {JSX.Element} Card displaying formatted news item
 */
const FeedCard = ({ item, searchTerm }) => {
  /**
   * Extract clean text from HTML description
   * Parses HTML and returns plain text without tags
   * 
   * @param {string} html - HTML string from feed description
   * @returns {string} Plain text summary
   */
  const getSummary = (html) => {
    // Parse HTML string into document object
    const doc = new DOMParser().parseFromString(html, "text/html");
    // Return text content, stripping all HTML tags
    return doc.body.textContent || "";
  };

  const summaryText = getSummary(item.description);

  return (
    <Card style={{ margin: "0 15px", borderColor: "transparent" }}>
      <Card.Body>
        <Container fluid>
          <Row>
            <Col
              xs={item.img ? 10 : 12}
              sm={item.img ? 10 : 12}
              md={item.img ? 10 : 12}
              lg={item.img ? 10 : 12}
              xl={item.img ? 10 : 12}
              xxl={item.img ? 8 : 12}
            >
              <a href={item.link} target="_blank" rel="noopener noreferrer">
                <p
                  className="dashboardCardData rssFeedHeader"
                  style={{ padding: "0", fontSize: "18px" }}
                >
                  {highlightText(item.title, searchTerm)}
                </p>
              </a>

              <p
                className="dashboardCardLabel"
                style={{ padding: "0", margin: "0" }}
              >
                {new Date(item.pubDate).toLocaleDateString()}
              </p>

              <p
                className="dashboardCardText rssFeedHeader"
                style={{ padding: "0", margin: "0" }}
              >
                {highlightText(summaryText, searchTerm)}
              </p>
            </Col>
            <Col
              xs={2}
              sm={2}
              md={2}
              lg={2}
              xl={2}
              xxl={4}
              style={{ alignContent: "center" }}
            >
              {item.img && (
                <img
                  src={item.img.src}
                  alt={item.img.alt || item.title}
                  // Use placeholder image if the feed image fails to load
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src =
                      "https://placehold.co/96x96/eeeeee/333333?text=No+Img";
                  }}
                  style={{
                    width: "100%",
                    height: "7.5em",
                    objectFit: "contain",
                  }}
                />
              )}
            </Col>
          </Row>
        </Container>
      </Card.Body>
    </Card>
  );
};

// Main App Component
/**
 * OneColFeedDisplay Component
 * 
 * Advanced news feed display with pagination, search filtering, and date filtering.
 * Fetches financial and corporate press release news via Cision API based on page route.
 * Supports keyword search highlighting and date-based filtering.
 * Manages pagination with customizable page size and scroll-to-top functionality.
 * 
 * @param {Object} props - Component props
 * @param {Function} props.setAlert - Function to control alert visibility
 * @param {Function} props.setMessage - Function to set alert message content
 * @param {Function} props.closeAlert - Function to hide alert
 * @returns {JSX.Element} News feed with pagination, search, filters, and news cards
 */
const OneColFeedDisplay = (props) => {
  const { supabase } = useSupabaseApp();

  // State to store financial news feed items from Cision API
  const [finItems, setFinItems] = useState([]);

  // State to store corporate press release items
  const [cprItems, setCprItems] = useState([]);

  // State to track pagination metadata for financial news
  const [finMeta, setFinMeta] = useState(null);

  // State to track pagination metadata for CPR news
  const [cprMeta, setCprMeta] = useState(null);

  // State to track current page number for pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Number of items per page
  const pageSize = 5;

  // State to track whether feed data is currently loading
  const [isLoading, setIsLoading] = useState(true);

  // Reference to feed container for scroll-to-top functionality
  const scrollRef = useRef(null);

  // State to store selected date filter value
  const [selectedDate, setSelectedDate] = useState("");

  // State to store search query term for filtering articles
  const [searchTerm, setSearchTerm] = useState("");

  // Get current page route to determine which feed type to display
  const location = useLocation();
  const pathname = location.pathname;

  // Get metadata for current feed type (financial or CPR)
  const current =
    pathname === "/prnewswire-fin"
      ? finMeta
      : pathname === "/prnewswire-cpr"
        ? cprMeta
        : null;

  // Number of items to fetch per API request
  const slice = 30;

  /**
   * Fetch news feed data from Cision API endpoint
   * Supports pagination, search filtering, and date filtering
   * Updates feed items and metadata state with API response
   */
  useEffect(() => {
    async function getNewsFeed() {
      setIsLoading(true);
      // Show loading alert
      props.setAlert(true);
      props.setMessage(
        <div style={{ display: "flex", paddingTop: "10px" }}>
          <div style={{ width: "90%" }}>
            <p style={{ color: "#0f9d58" }}>
              <BiAnalyse className="alert-icon" /> Loading
            </p>
            <p style={{ marginLeft: "28px", fontSize: "14px" }}>
              Won't be long now. We're always doing our best to reduce waiting
              time.
            </p>
            <p style={{ marginLeft: "28px", fontSize: "14px" }}>
              If your waiting time is too long,{" "}
              <a
                href="https://www.roiquant.com/contact"
                target="_blank"
                rel="noopener noreferrer"
              >
                contact us
              </a>
              .
            </p>
          </div>
          <div style={{ width: "10%", textAlign: "right" }}>
            <Spinner animation="border" variant="secondary" />
          </div>
        </div>
      );

      try {
        // Call Cision API with pagination, search, and date parameters
        const { data, error } = await supabase.functions.invoke(
          "getCisionNewsAll",
          {
            body: {
              numOfRows: pageSize,
              currentPage,
              searchTerm,
              from: selectedDate ? selectedDate : "",
              to: "",
            },
          }
        );

        if (error) {
          const ctx = await error.context?.json?.().catch(() => ({}));
          const msg = ctx?.error ?? "unknown";
          throw new Error(msg);
        }

        // // 1. Filtering Logic: Filter items that include 'FIN' in their category array
        // finItems = setFinItems(data.filter(item => item.category.includes('FIN')));

        // // 2. Filtering Logic: Filter items that include 'CPR' in their category array
        // cprItems = setCprItems(data.filter(item => item.category.includes('CPR')));

        // 1. Calculate the filtered arrays. Added defensive check for item.category existence.
        const newFinItems = data.finItems.items;
        const newCprItems = data.cprItems.items;

        // 2. ONLY call the state setters.
        setFinItems(newFinItems);
        setCprItems(newCprItems);

        // 3. store pagination metadata
        setFinMeta({
          currentPage: data.finItems.currentPage,
          totalPages: data.finItems.totalPages,
          totalItems: data.finItems.totalItems,
          pageSize: data.finItems.pageSize,
        });

        setCprMeta({
          currentPage: data.cprItems.currentPage,
          totalPages: data.cprItems.totalPages,
          totalItems: data.cprItems.totalItems,
          pageSize: data.cprItems.pageSize,
        });
        setIsLoading(false);
        props.closeAlert();
      } catch (error) {
        setIsLoading(false);
        props.setAlert(true);
        props.setMessage(
          <div style={{ paddingTop: "10px" }}>
            <p style={{ fontSize: "16px", color: "red" }}>
              <AiOutlineCloseCircle className="alert-icon" /> Error
            </p>
            <p style={{ marginLeft: "28px", fontSize: "14px" }}>
              An unknown error occurred. Please try again. If error persists,{" "}
              <a
                href="https://www.roiquant.com/contact"
                target="_blank"
                rel="noopener noreferrer"
              >
                contact us
              </a>
              .
            </p>
          </div>
        );
      }
    }

    getNewsFeed();
  }, [currentPage, searchTerm, selectedDate]);

  useEffect(() => {
    if (!isLoading && scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [isLoading]);

  useEffect(() => {
    // as an overscroll
    const el = document.querySelector(".rssFeedSharedScroll");
    if (!el) return;

    const MAX_STEP = 150;

    const onWheel = (e) => {
      const atTop = el.scrollTop <= 0;
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;

      const scrollingUp = e.deltaY < 0;
      const scrollingDown = e.deltaY > 0;

      if ((atTop && scrollingUp) || (atBottom && scrollingDown)) {
        e.preventDefault();

        const step = Math.max(-MAX_STEP, Math.min(MAX_STEP, e.deltaY));

        window.scrollBy({
          top: step,
          behavior: "auto",
        });
      }
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  const SkeletonCard = () => (
    <div className="skeleton-wrapper">
      <div className="skeleton-line long"></div>
      <div className="skeleton-line short"></div>
      <div
        style={{
          width: "100%",
          borderBottom: "1px solid #e5e5e5",
          margin: "15px 0",
        }}
      />
    </div>
  );

  return (
    <div className="rssFeedPage">
      <Row
        className="d-flex justify-content-between align-items-center rssFeedRow"
        style={{
          paddingTop: "50px",
          paddingBottom: "10px",
          margin: "0 0 16px 0",
        }}
      >
        <p
          className="dashboardHeader"
          style={{
            margin: 0,
            // fontSize: "18px",
            // fontWeight: 500,
            whiteSpace: "nowrap",
            padding: "0",
          }}
        >
          <img src={PRNewswireLogo} style={{ marginTop: "-6px" }} /> is the
          official News Distribution Partner of roiquant
        </p>

        <p
          className="dashboardHeader"
          style={{
            margin: 0,
            // fontSize: "18px",
            // fontWeight: 500,
            whiteSpace: "nowrap",
            padding: "0",
          }}
        >
          {pathname === "/prnewswire-fin"
            ? "Financial Services"
            : "Business Technology"}
        </p>
      </Row>

      <Row className="rssFeedContainer">
        <Row className="rssFilterBar g-0 px-0 mx-0">
          <Col xs="auto" className="p-0">
            <input
              type="date"
              className="rssFilterInput"
              onChange={(e) => {
                setSelectedDate(e.target.value);
                setCurrentPage(1);
              }}
            />
          </Col>

          <Col xs="auto" className="p-0 ms-auto">
            <input
              type="text"
              placeholder="Search news"
              className="rssFilterInput"
              style={{ width: "600px" }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setSearchTerm(e.target.value.trim());
                  setCurrentPage(1);
                }
              }}
            />
          </Col>
        </Row>
        <div
          style={{
            width: "100%",
            borderBottom: "1px solid #e5e5e5",
            margin: "15px 0",
          }}
        />

        <div
          ref={scrollRef}
          className={`rssFeedSharedScroll ${isLoading ? "loading" : ""}`}
        >
          {pathname === "/prnewswire-fin" ? (
            <Col>
              <Row className="rssFeedSubContainer">
                {isLoading ? (
                  Array.from({ length: slice }).map((_, i) => (
                    <Col xs={12} key={i}>
                      <SkeletonCard />
                    </Col>
                  ))
                ) : finItems && finItems.length > 0 ? (
                  finItems.slice(0, slice).map((item, index) => (
                    <React.Fragment key={index}>
                      <Row style={{ marginBottom: "15px" }}>
                        <FeedCard item={item} searchTerm={searchTerm} />
                      </Row>
                      {index !== slice - 1 && (
                        <div
                          style={{
                            width: "100%",
                            borderBottom: "1px solid #e5e5e5",
                            margin: "15px 0",
                          }}
                        />
                      )}
                    </React.Fragment>
                  ))
                ) : (
                  <p className="dashboardHeader">No data found.</p>
                )}
              </Row>
            </Col>
          ) : (
            <Col>
              <Row className="rssFeedSubContainer">
                {isLoading ? (
                  Array.from({ length: slice }).map((_, i) => (
                    <SkeletonCard key={i} />
                  ))
                ) : cprItems && cprItems.length > 0 ? (
                  cprItems.slice(0, slice).map((item, index) => (
                    <React.Fragment key={index}>
                      <Row style={{ marginBottom: "15px" }}>
                        <FeedCard item={item} searchTerm={searchTerm} />
                      </Row>
                      {index !== slice - 1 && (
                        <div
                          style={{
                            width: "100%",
                            borderBottom: "1px solid #e5e5e5",
                            margin: "15px 0",
                          }}
                        />
                      )}
                    </React.Fragment>
                  ))
                ) : (
                  <p className="dashboardHeader">No data found.</p>
                )}
              </Row>
            </Col>
          )}
        </div>
        <div
          style={{
            width: "100%",
            borderBottom: "1px solid #e5e5e5",
            margin: "15px 0",
          }}
        />
        <Row className="rssFilterBar g-0 px-0 mx-0">
          <Col className="d-flex justify-content-center">
            {!isLoading && current && (
              <Pagination>
                <Pagination.First
                  disabled={current.currentPage === 1}
                  onClick={() => setCurrentPage(1)}
                />

                <Pagination.Prev
                  disabled={current.currentPage === 1}
                  onClick={() => setCurrentPage(current.currentPage - 1)}
                />

                {Array.from({ length: current.totalPages }, (_, i) => i + 1)
                  .filter(
                    (p) =>
                      Math.abs(p - current.currentPage) <= 2 ||
                      p === 1 ||
                      p === current.totalPages
                  )
                  .map((page, i, arr) => {
                    const prev = arr[i - 1];
                    const gap = prev && page - prev > 1;

                    return (
                      <React.Fragment key={page}>
                        {gap && <Pagination.Ellipsis disabled />}
                        <Pagination.Item
                          active={page === current.currentPage}
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </Pagination.Item>
                      </React.Fragment>
                    );
                  })}

                <Pagination.Next
                  disabled={current.currentPage === current.totalPages}
                  onClick={() => setCurrentPage(current.currentPage + 1)}
                />

                <Pagination.Last
                  disabled={current.currentPage === current.totalPages}
                  onClick={() => setCurrentPage(current.totalPages)}
                />
              </Pagination>
            )}
          </Col>
        </Row>
      </Row>
    </div>
  );
};

export default OneColFeedDisplay;
